import React, { useState, useEffect } from "react";
import { Gear, UserCircleGear, UserGear } from "phosphor-react";
import { Nav_Buttons } from "../../data";
import {
  Avatar,
  Box,
  Divider,
  IconButton,
  Menu,
  Tooltip,
  Stack,
  useTheme,
} from "@mui/material";
import useSettings from "../../hooks/useSettings";
import MaterialUISwitch from "../../components/ThemeSwitch";
import Logo from "../../assets/Images/logo.ico";
import ToggleButton from "../../components/settings/drawer/ToggleButton";
import { useNavigate } from "react-router-dom";
import SettingsDrawer from "../../components/settings/drawer";

const getPath = (index) => {
  switch (index) {
    case 0:
      return "/app";
    case 1:
      return "/group";
    case 2:
      return "/call";
    case 3:
      return "/settings";
    case 4:
      return "/admin";
    default:
      return "/";
  }
};

const SideBar = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { themeMode, onToggleMode } = useSettings();

  const [selected, setSelected] = useState(null); // Initially set to null
  const [loading, setLoading] = useState(true); // Loading state to delay rendering
  const [role, setRole] = useState(null); // Role state to conditionally render Admin
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const [toggleOpen, setToggleOpen] = useState(false); // ✅ ToggleButton state

  const handleToggle = () => {
    setToggleOpen((prev) => !prev);
  };

  useEffect(() => {
    if (toggleOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
  }, [toggleOpen]);


  // Fetch role and selected tab from localStorage on initial load
  useEffect(() => {
    const savedTab = localStorage.getItem("selectedTab");
    const userRole = localStorage.getItem("role"); // Fetch role from localStorage

    if (userRole) {
      setRole(parseInt(userRole)); // Parse role as integer
    }

    if (savedTab !== null) {
      setSelected(parseInt(savedTab));
      navigate(getPath(parseInt(savedTab))); // Navigate to the saved tab on load
    } else {
      setSelected(0); // Default to the first tab if no saved tab is found
    }
    setLoading(false); // Loading complete
  }, [navigate]);

  // const handleClick = (event) => {
  //   setAnchorEl(event.currentTarget);
  // };
  // const handleClose = () => {
  //   setAnchorEl(null);
  // };



  const handleChangeTab = (index) => {
    setSelected(index);
    localStorage.setItem("selectedTab", index); // Save the selected tab in localStorage
    navigate(getPath(index));
  };

  // Show a loading spinner or nothing while loading the saved tab
  if (loading) {
    return null; // Or return a loading spinner component if needed
  }

  return (
    <>
      <Box
        py={2}
        px={0}
        sx={{
          backgroundColor: theme.palette.background.paper,
          height: "auto",
          width: 80,
          boxShadow: "inset -1px 0px 0px rgba(0, 0, 0, 0.15)",
        }}
      >
        <Stack
          direction="column"
          alignItems={"center"}
          sx={{ height: "100%", width: 80, }}
          spacing={3}

          justifyContent={"space-between"}
        >
          <Stack alignItems={"center"} spacing={4}>
            <Box
              sx={{
                // backgroundColor: theme.palette.primary.main,
                height: 40,
                width: 40,
                borderRadius: 1.5,
              }}
            >
              <img src="aabhyasa_logo_element.png" alt={"aabhyasa_logo"} />
            </Box>
            <Divider sx={{ width: "48px",bgcolor: theme.palette.mode === "light" ? "#666" : "#ddd" }} />
            <Stack
              spacing={2}
              direction="column"
              alignItems={"center"}
              sx={{ width: "max-content" }}
            >
              {Nav_Buttons.map((el) =>
                el.index === selected ? (
                  <Box
                    sx={{
                      backgroundColor: theme.palette.primary.main,
                      borderRadius: 1.5,
                      height: 54,
                      width: 54,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                    key={el.index}
                  >
                    <IconButton
                      onClick={() => handleChangeTab(el.index)}
                      sx={{ width: "max-content", color: "#fff" }}
                    >
                      {el.icon}
                    </IconButton>
                  </Box>
                ) : (
                  <Tooltip title={el.title} placement="right" key={el.index}>
                    <IconButton
                      onClick={() => handleChangeTab(el.index)}
                      sx={{
                        width: "max-content",
                        color:
                          theme.palette.mode === "light"
                            ? "#000"
                            : theme.palette.text.primary,
                      }}
                    >
                      {el.icon}
                    </IconButton>
                  </Tooltip>
                )
              )}
              <Divider sx={{ width: "48px" }} />
              {/* Conditionally render Admin button based on role */}
              {role !== 3 && (
                selected === 4 ? (
                  <Box
                    sx={{
                      backgroundColor: theme.palette.primary.main,
                      borderRadius: 1.5,
                      height: 64,
                      width: 64,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <IconButton sx={{ width: "max-content", color: "#fff" }}>
                      <UserCircleGear />
                    </IconButton>
                  </Box>
                ) : (
                  <Tooltip title="Admin" placement="right">
                    <IconButton
                      onClick={() => handleChangeTab(4)}
                      sx={{
                        width: "max-content",
                        color:
                          theme.palette.mode === "light"
                            ? "#000"
                            : theme.palette.text.primary,
                      }}
                    >
                      <UserCircleGear />
                    </IconButton>
                  </Tooltip>
                )
              )}
            </Stack>
          </Stack>

          <Stack alignItems={"center"} spacing={2}>
            <ToggleButton
              notDefault={true}
              open={toggleOpen}
              onToggle={handleToggle}
            />
            <SettingsDrawer open={toggleOpen} setOpen={setToggleOpen} />
            <MaterialUISwitch
              checked={themeMode === "dark"}
              onChange={() => {
                onToggleMode();
              }}
            />
            <Tooltip title="Profile" placement="right">
              <IconButton
                id="profile-button"
                aria-controls={open ? "profile-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={open ? "true" : undefined}
                onClick={() => handleChangeTab(3)}
                sx={{
                  width: "max-content",
                  color:
                    theme.palette.mode === "light"
                      ? "#000"
                      : theme.palette.text.primary,
                      borderRadius: "50%",
                      border: theme.palette.mode === "light"
                      ?  "1px solid #000" : "1px solid #ddd"
                }}
              >
                <UserGear size={18}  />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

      </Box>

    </>
  );
};

export default SideBar;
